import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import FormControl from 'react-bootstrap/FormControl';
import Image from 'react-bootstrap/Image';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import { ContentCategoriesQuery } from '../../../../../../__gqltypes__';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import { UPLOAD_ASSET, UPLOAD_ICON_ASSET } from '../../../components/apolloQueries';
import SelectDropdown from '../../../components/SelectDropdown';
import { AVAILABILITY_TYPES, CURRENCY_TYPES } from '../utils';
import AssetDisplay from './AssetDisplay';
import DragUpload from './DragUpload';
import { Item, OnValidateItem, SetItemData } from './typedef';
import VinylThemeGenerator from '../vinylTheme/VinylThemeGenerator';

type Props = {
  itemData: Item;
  setItemData: SetItemData;
  refetch: () => void;
  categories: ContentCategoriesQuery['app']['contentCategories'];
  onValidate?: OnValidateItem;
  onAddLevel: () => void;
  onRemoveLevel: () => void;
};

const ellipsis: React.CSSProperties = {
  display: 'inline-block',
  maxWidth: 340,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'bottom',
};

export default function VinylEditor({
  itemData,
  setItemData,
  refetch,
  categories,
  onValidate,
  onAddLevel,
  onRemoveLevel,
}: Props) {
  const [uploadAsset, { loading: uploadLoading }] = useMutation(UPLOAD_ASSET);
  const [uploadIconAsset, { loading: iconLoading }] = useMutation(UPLOAD_ICON_ASSET);
  const [preview, setPreview] = useState<string | null>(null);

  // Upload a single level's asset and merge ONLY that level, so unsaved edits on the other levels
  // (name, auto-filled theme URLs, prices) survive. We don't resync from refetch for the same vinyl.
  const handleUploadLevel = (level: number) => async (file: Blob) => {
    const { data } = await uploadAsset({ variables: { input: { file, id: itemData.id, level } } });
    const it = data?.uploadAsset?.item;
    if (level === 1) {
      const asset = it?.asset;
      // @ts-ignore - Item is a loose union here
      if (asset) setItemData((prev) => ({ ...prev, asset }));
    } else {
      const fresh = it?.nextLevelItems?.find((n: { level: number }) => n.level === level);
      if (fresh?.asset) {
        // @ts-ignore
        setItemData((prev) => ({
          ...prev,
          // @ts-ignore
          nextLevelItems: prev.nextLevelItems.map((u) => (u.level === level ? { ...u, asset: fresh.asset } : u)),
        }));
      }
    }
    refetch();
  };

  const handleIconUpload = async (file: Blob) => {
    const { data } = await uploadIconAsset({ variables: { input: { file, id: itemData.id } } });
    const iconAsset = data?.uploadIconAsset?.item?.iconAsset;
    // @ts-ignore
    if (iconAsset) setItemData((prev) => ({ ...prev, iconAsset }));
    refetch();
  };

  // @ts-ignore
  const onAcquisitionSelect = (e) => {
    // @ts-ignore
    setItemData({
      ...itemData,
      liveOpsAvailability: e.target.value,
      cost:
        // @ts-ignore
        AVAILABILITY_TYPES[e.target.value].length > 0
          ? e.target.value === 'UNAVAILABLE'
            ? // @ts-ignore
              itemData.cost || { __typename: 'CurrencyCategoryFragment', cost: 0 }
            : // @ts-ignore
              { ...itemData.cost, __typename: CURRENCY_TYPES[AVAILABILITY_TYPES[e.target.value][0]] }
          : null,
    });
  };

  const setBaseAmount = (amount: string) =>
    // @ts-ignore
    setItemData({ ...itemData, cost: { ...itemData.cost, amount } });

  const setLevelAmount = (level: number, amount: string) =>
    setItemData({
      ...itemData,
      // @ts-ignore
      nextLevelItems: itemData.nextLevelItems.map((u) =>
        u.level === level ? { ...u, cost: { ...u.cost, amount } } : u
      ),
    });

  const onThemeUploaded = (files: { filename: string; url: string }[]) => {
    // level 1 = base item asset, level >=2 = an upgrade in nextLevelItems.
    const urlForLevel = (level: number) =>
      files.find((f) => new RegExp(`ThemeLvl${level}\\.json$`, 'i').test(f.filename))?.url;
    const lvl1Url = urlForLevel(1);
    // @ts-ignore
    const nextLevelItems = (itemData.nextLevelItems ?? []).map((up) => {
      // @ts-ignore
      const url = urlForLevel(up.level);
      return url ? { ...up, asset: url } : up;
    });
    // Auto-create rows for theme levels (>=2) that don't have an upgrade yet, so they appear at once.
    for (let level = nextLevelItems.length + 2; ; level += 1) {
      const url = urlForLevel(level);
      if (!url) break;
      nextLevelItems.push({
        id: `${itemData.id}.${level - 1}`,
        level,
        // @ts-ignore
        cost: { amount: 0 },
        asset: url,
        isNewUpgrade: true,
      });
    }
    setItemData({
      ...itemData,
      ...(lvl1Url ? { asset: lvl1Url } : {}),
      // @ts-ignore
      nextLevelItems,
    });
  };

  const categoryOptions: Record<string, { value: React.ReactNode; searchValue: string }> = {};
  categories.forEach((category) => {
    categoryOptions[category.id] = {
      value: (
        <>
          <Image roundedCircle className="img-profile mr-3" src={category.icon} style={{ width: 20, height: 20 }} />
          {category.name}
        </>
      ),
      searchValue: category.name,
    };
  });

  // A purchasable level needs a price > 0 (ItemView blocks Save otherwise); flag the offending field.
  const badAmount = (amount: unknown) => {
    const n =
      amount === '' || amount === null || amount === undefined
        ? NaN
        : typeof amount === 'number'
        ? amount
        : parseInt(String(amount), 10);
    return Number.isNaN(n) || n <= 0;
  };

  const avail = itemData.liveOpsAvailability;
  // @ts-ignore
  const currencyKeys: string[] = AVAILABILITY_TYPES[avail] || [];
  const hasCost = !!itemData.cost;
  // @ts-ignore
  const isFragment = itemData.cost?.__typename === CURRENCY_TYPES.CURRENCY_CATEGORY_FRAGMENT;

  const levelRows = [
    // @ts-ignore
    { level: 1, label: 'Base item', asset: itemData.asset, amount: itemData.cost?.amount, base: true, isNew: false },
    // @ts-ignore
    ...itemData.nextLevelItems.map((u) => ({
      level: u.level,
      label: `Item level ${u.level}`,
      asset: u.asset,
      amount: u.cost?.amount,
      base: false,
      // @ts-ignore
      isNew: !!u.isNewUpgrade,
    })),
  ];

  return (
    <>
      <Row>
        {/* ---- Details ---- */}
        <Col md={4}>
          <Form>
            <Form.Group>
              <Form.Label>Id</Form.Label>
              <Form.Control placeholder={itemData.id} disabled />
            </Form.Group>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={itemData.name}
                onChange={(e) => setItemData({ ...itemData, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Acquisition</Form.Label>
              <Form.Control as="select" value={avail} onChange={onAcquisitionSelect}>
                {Object.keys(AVAILABILITY_TYPES).map((type) => (
                  <option value={type} key={type}>
                    {type}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            {hasCost && (
              <Form.Group>
                <Form.Label>Currency</Form.Label>
                {currencyKeys.length > 1 ? (
                  <Form.Control
                    as="select"
                    // @ts-ignore
                    value={itemData.cost.__typename}
                    onChange={(e) =>
                      // @ts-ignore
                      setItemData({ ...itemData, cost: { ...itemData.cost, __typename: e.target.value } })
                    }
                  >
                    {currencyKeys.map((key) => (
                      // @ts-ignore
                      <option value={CURRENCY_TYPES[key]} key={key}>
                        {/* @ts-ignore */}
                        {CURRENCY_TYPES[key]}
                      </option>
                    ))}
                  </Form.Control>
                ) : (
                  // @ts-ignore
                  <Form.Control disabled value={CURRENCY_TYPES[currencyKeys[0]]} />
                )}
                <Form.Text className="text-muted">Applies to every level — set each price in the table.</Form.Text>
              </Form.Group>
            )}
            {isFragment && (
              <Form.Group>
                <Form.Label>Fragment currency</Form.Label>
                <SelectDropdown
                  title="Select fragment currency"
                  options={categoryOptions}
                  // @ts-ignore
                  value={itemData.cost?.category ? itemData.cost.category.id : null}
                  onSelect={(v) =>
                    // @ts-ignore
                    setItemData({ ...itemData, cost: { ...itemData.cost, category: { id: v } } })
                  }
                />
              </Form.Group>
            )}
          </Form>

          <hr />
          <Form.Label>Vinyl icon</Form.Label>
          <DragUpload onUpload={handleIconUpload} preventSubmit showSelectedFile compact />
          <LoadingLogo show={iconLoading} />
          {/* @ts-ignore */}
          {itemData.iconAsset && <AssetDisplay className="mt-2" assetUrl={itemData.iconAsset} />}
          <Row className="mt-2">
            <Col>
              <Form.Label className="small text-muted">Primary</Form.Label>
              <Form.Control
                type="text"
                placeholder="#RRGGBB"
                // @ts-ignore
                value={itemData.iconPrimaryColor || ''}
                // @ts-ignore
                onChange={(e) => setItemData({ ...itemData, iconPrimaryColor: e.target.value })}
              />
            </Col>
            <Col>
              <Form.Label className="small text-muted">Background</Form.Label>
              <Form.Control
                type="text"
                placeholder="#RRGGBB"
                // @ts-ignore
                value={itemData.iconBackgroundColor || ''}
                // @ts-ignore
                onChange={(e) => setItemData({ ...itemData, iconBackgroundColor: e.target.value })}
              />
            </Col>
          </Row>
        </Col>

        {/* ---- Theme pipeline ---- */}
        <Col md={8}>
          <VinylThemeGenerator key={itemData.id} itemId={itemData.id} onThemeUploaded={onThemeUploaded} />
        </Col>
      </Row>

      {/* ---- Levels ---- */}
      <hr />
      <LoadingLogo show={uploadLoading} />
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Levels</h5>
        <div>
          <Button variant="success" size="sm" className="mr-2" onClick={onAddLevel}>
            <i className="fas fa-plus-circle mr-1" /> Add level
          </Button>
          <Button variant="danger" size="sm" onClick={onRemoveLevel}>
            <i className="fas fa-minus-circle mr-1" /> Remove
          </Button>
        </div>
      </div>
      <Table bordered size="sm" responsive className="align-middle">
        <thead>
          <tr>
            <th style={{ width: 44 }}>Lvl</th>
            <th style={{ width: 120 }}>Maps to</th>
            <th>Asset</th>
            {hasCost && <th style={{ width: 110 }}>Price</th>}
            <th style={{ width: 220 }}>Upload</th>
          </tr>
        </thead>
        <tbody>
          {levelRows.map((r) => (
            <tr key={r.level}>
              <td className="font-weight-bold text-primary">{r.level}</td>
              <td>
                {r.label}
                {r.isNew && <div className="small text-muted">unsaved</div>}
              </td>
              <td>
                {r.asset ? (
                  <div className="d-flex align-items-center">
                    <a href={r.asset} target="_blank" rel="noopener noreferrer" style={ellipsis}>
                      {r.asset}
                    </a>
                    <Button variant="link" size="sm" className="p-0 ml-2" onClick={() => setPreview(r.asset)}>
                      Preview
                    </Button>
                  </div>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              {hasCost && (
                <td>
                  {r.base ? (
                    <FormControl
                      size="sm"
                      value={r.amount ?? ''}
                      isInvalid={badAmount(r.amount)}
                      onChange={(e) => setBaseAmount(e.target.value)}
                    />
                  ) : (
                    <FormControl
                      size="sm"
                      value={r.amount ?? ''}
                      isInvalid={badAmount(r.amount)}
                      onChange={(e) => setLevelAmount(r.level, e.target.value)}
                    />
                  )}
                </td>
              )}
              <td>
                {r.isNew ? (
                  <span className="small text-muted">Save the item to enable upload.</span>
                ) : (
                  <DragUpload
                    onUpload={handleUploadLevel(r.level)}
                    onValidate={onValidate}
                    preventSubmit
                    showSelectedFile
                    compact
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={preview !== null} onHide={() => setPreview(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-truncate">Asset preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {preview && (
            <>
              <p className="small text-break">
                <a href={preview} target="_blank" rel="noopener noreferrer">
                  {preview}
                </a>
              </p>
              {preview.endsWith('.png') && <AssetDisplay assetUrl={preview} />}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
